import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TransactionService } from '../../services/transaction.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-transaction-details',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './transaction-details.component.html',
  styleUrl: './transaction-details.component.less'
})
export class TransactionDetailsComponent implements OnInit {
  searchQuery: string = '';
  filteredTransactions: any[] = [];
  transactions: any[] = [];
  userId: string = '';
  
  // Pagination properties
  currentPage: number = 1;
  totalPages: number = 0;
  totalRecords: number = 0;
  limit: number = 5;
  
  // Keep track of current search to maintain it during pagination
  currentSearch: string = '';

  constructor(private transactionService: TransactionService, private userService: UserService) { }

  noTransactionsMessage: string = 'No transactions found matching your search criteria.';

  ngOnInit(): void {
    this.fetchUserId();
  }

  fetchUserId() {
    this.userService.getCurrentUser().subscribe({
      next: (response) => {
        console.log("success.component.ts [fbdc58] fetchUserId: ", response);
        this.userId = response._id;
        this.loadTransactions();
      },
      error: (error) => {
        console.error("Error fetching userId", error);
      }
    })
  }

  loadTransactions(page: number = 1, searchQuery?: string): void {
    this.currentPage = page;
    
    // Use provided searchQuery or maintain current search
    const search = searchQuery !== undefined ? searchQuery : this.currentSearch;
    this.currentSearch = search;

    this.transactionService.getUserTransactions(this.userId, search, page, this.limit).subscribe({
      next: (res: any) => {
        this.transactions = res.data || [];
        this.filteredTransactions = [...this.transactions];
        this.totalPages = res.totalPages || 0;
        this.totalRecords = res.totalRecords || 0;
        
        if (this.transactions.length === 0) {
          this.noTransactionsMessage = search ? 'No transactions found matching your search criteria.' : 'No transactions found.';
        }
        console.log('Transactions fetched successfully:', this.filteredTransactions);
      },
      error: (error: any) => {
        console.error('Error fetching transactions:', error);
        this.noTransactionsMessage = 'Failed to load transactions. Please try again later.';
      }
    });
  }

  onSearchChange(): void {
    const query = this.searchQuery.trim();
    this.currentSearch = query;
    // Always start from page 1 when searching
    this.loadTransactions(1, query);
  }

  // Clear search functionality
  clearSearch(): void {
    this.searchQuery = '';
    this.currentSearch = '';
    this.loadTransactions(1, '');
  }

  // Pagination methods - now properly maintain search query
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.loadTransactions(page);
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  // Generate array for pagination buttons
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    
    let startPage = Math.max(1, this.currentPage - halfRange);
    let endPage = Math.min(this.totalPages, this.currentPage + halfRange);
    
    // Adjust if we're near the beginning or end
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (startPage === 1) {
        endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === this.totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  getStatusClass(status: string | null | undefined): string {
    if (!status) return '';

    // Normalize status to lowercase for consistent matching
  switch (status.toLowerCase()) {
    case 'completed':
    case 'paid':
      return 'status-completed';
    case 'pending':
      return 'status-pending';
    case 'failed':
      return 'status-failed';
    default:
      return '';
  }
}

  getAmountClass(amount: number): string {
    return amount >= 0 ? 'amount-positive' : 'amount-negative';
  }

  // Format amount to include a sign and two decimal places
  formatAmount(amount: number): string {
    const sign = amount >= 0 ? '+' : '';
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  viewTransactionDetails(transaction: any): void {
    if (transaction.invoice_url) {
      window.open(transaction.invoice_url, '_blank');
    } else {
      console.warn('No invoice URL available for this transaction.');
    }
  }

  retryPayment(transaction: any): void {
    console.log('Retrying payment for transaction:', transaction.id);
  }
}